import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import
{
    ArrowRight,
    BarChartFill,
    ChatDotsFill,
    CheckCircleFill,
    Clipboard2CheckFill,
    DatabaseFill,
    FileEarmarkCodeFill,
    FileEarmarkTextFill,
    GearFill,
    JournalBookmarkFill,
    LayersFill,
    LightningChargeFill,
    PlayCircleFill,
    ShieldCheck,
    StarFill
} from 'react-bootstrap-icons';
import { useAuth } from '../contexts/AuthContext';
import { LandingFooter, LandingHeader } from '../components/LandingSiteChrome';
import './LandingDaylight.css';

interface CountUpProps
{
    end: number;
    decimals?: number;
    suffix?: string;
    prefix?: string;
}

function CountUp({ end, decimals = 0, suffix = '', prefix = '' }: CountUpProps): React.ReactElement
{
    const [value, setValue] = useState(0);
    const nodeRef = useRef<HTMLSpanElement | null>(null);
    const startedRef = useRef(false);

    useEffect(() =>
    {
        const node = nodeRef.current;
        if (!node)
        {
            return undefined;
        }
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches)
        {
            setValue(end);
            return undefined;
        }
        const observer = new IntersectionObserver((entries) =>
        {
            if (!entries[0].isIntersecting || startedRef.current)
            {
                return;
            }
            startedRef.current = true;
            const duration = 1600;
            const start = performance.now();
            function tick(now: number): void
            {
                const progress = Math.min((now - start) / duration, 1);
                const eased = 1 - Math.pow(1 - progress, 3);
                setValue(end * eased);
                if (progress < 1)
                {
                    requestAnimationFrame(tick);
                }
            }
            requestAnimationFrame(tick);
        }, { threshold: 0.4 });
        observer.observe(node);
        return () => observer.disconnect();
    }, [end]);

    return (
        <span ref={nodeRef}>
            {prefix}
            {value.toFixed(decimals)}
            {suffix}
        </span>
    );
}

interface MascotImageProps
{
    sources: string[];
    alt: string;
    className?: string;
}

function MascotImage({ sources, alt, className = '' }: MascotImageProps): React.ReactElement
{
    const [sourceIndex, setSourceIndex] = useState(0);

    if (sourceIndex >= sources.length)
    {
        return (
            <div className={`ll-mascot-fallback ${className}`} role="img" aria-label={alt}>
                <GearFill />
                <span>Place mascot image at<br /><code>{sources[0]}</code></span>
            </div>
        );
    }

    return (
        <img
            className={className}
            src={sources[sourceIndex]}
            alt={alt}
            onError={() => setSourceIndex((index) => index + 1)}
            draggable={false}
        />
    );
}

const WINDOW_ROWS: Array<{ name: string; status: 'passed' | 'healed' | 'review' }> = [
    { name: 'TC-014 · Checkout with saved card', status: 'passed' },
    { name: 'TC-015 · Coupon at order review', status: 'healed' },
    { name: 'TC-016 · Address change mid-flow', status: 'passed' },
    { name: 'TC-017 · Payment timeout retry', status: 'review' }
];

interface PlatformFeature
{
    icon: typeof Clipboard2CheckFill;
    iconClass: string;
    tag: string;
    title: string;
    description: string;
    highlights: string[];
    demo: React.ReactNode;
}

const PLATFORM_FEATURES: PlatformFeature[] = [
    {
        icon: FileEarmarkTextFill,
        iconClass: 'll-ic-blue',
        tag: 'Understand',
        title: 'Requirements Intelligence',
        description: 'Upload documents or sync Azure DevOps work items. Agents extract structured, reviewable requirements with traceability back to the source line.',
        highlights: ['Traceable to source line', 'Sentinel-reviewed extraction', 'Azure DevOps sync'],
        demo: (
            <div className="ll-req-demo">
                <div className="ll-req-doc">
                    <span />
                    <span />
                    <span />
                </div>
                <ArrowRight />
                <div className="ll-req-list">
                    {['REQ-01 Payment retry', 'REQ-02 Coupon rules', 'REQ-03 Address change'].map((item) => (
                        <div key={item}>
                            <CheckCircleFill />
                            <span>{item}</span>
                        </div>
                    ))}
                </div>
            </div>
        )
    },
    {
        icon: JournalBookmarkFill,
        iconClass: 'll-ic-amber',
        tag: 'Understand',
        title: 'Domain & Enterprise Knowledge',
        description: 'Curated glossaries, systems, and conventions that ground every agent in how your business actually works, so generated artifacts speak your language.',
        highlights: ['Org-wide glossaries', 'System landscape aware', 'Feeds every agent'],
        demo: (
            <div className="ll-know-demo">
                {[
                    ['Domain glossary', '42 terms'],
                    ['Enterprise systems', 'SAP · Salesforce'],
                    ['Team conventions', '12 rules']
                ].map(([label, value]) => (
                    <div key={label}>
                        <span>{label}</span>
                        <em>{value}</em>
                    </div>
                ))}
            </div>
        )
    },
    {
        icon: Clipboard2CheckFill,
        iconClass: 'll-ic-blue',
        tag: 'Generate',
        title: 'AI Test Case Generation',
        description: 'Complete test cases with steps, expected results, and priority, drafted from your requirements in minutes and versioned as they evolve.',
        highlights: ['Steps and expected results', 'Priority and coverage tags', 'Versioned on change'],
        demo: (
            <div className="ll-tc-demo">
                <div className="ll-tc-head">
                    <strong>TC-042 · Apply coupon at checkout</strong>
                    <em className="ll-pill ll-pill-healed">High priority</em>
                </div>
                <ol>
                    <li>Add any item to the cart</li>
                    <li>Apply coupon SAVE10 at order review</li>
                    <li><b>Expected:</b> total reflects a 10% discount</li>
                </ol>
            </div>
        )
    },
    {
        icon: FileEarmarkCodeFill,
        iconClass: 'll-ic-violet',
        tag: 'Generate',
        title: 'Gherkin / BDD',
        description: 'Business-readable feature files ready for Cucumber, Behave, or SpecFlow, keeping engineers and stakeholders in the same story.',
        highlights: ['Cucumber / Behave / SpecFlow', 'Business-readable scenarios', 'Generated from test cases'],
        demo: (
            <pre className="ll-gherkin">
                <b>Given</b> a saved card{'\n'}
                <b>When</b> checkout is submitted{'\n'}
                <b>Then</b> the order is confirmed
            </pre>
        )
    },
    {
        icon: PlayCircleFill,
        iconClass: 'll-ic-green',
        tag: 'Execute',
        title: 'Execution Runs',
        description: 'Playwright agents run your suite in the browser and repair broken steps mid-run, with screenshots and logs for every step.',
        highlights: ['Real browser runs', 'Self-healing steps', 'Screenshots and logs'],
        demo: (
            <div className="ll-heal-demo">
                <em className="ll-pill ll-pill-review">Selector missing</em>
                <ArrowRight />
                <em className="ll-pill ll-pill-healed">Self-healed</em>
            </div>
        )
    },
    {
        icon: ChatDotsFill,
        iconClass: 'll-ic-blue',
        tag: 'Understand',
        title: 'Project Knowledge Chat',
        description: 'Ask anything about coverage, runs, and requirements. The chat knows your project, not just the internet.',
        highlights: ['Project-aware answers', 'Coverage gap detection', 'Drafts on request'],
        demo: (
            <div className="ll-chat-demo">
                <span className="ll-chat-q">Which requirements have no tests?</span>
                <span className="ll-chat-a">REQ-07 and REQ-12. Want me to draft them?</span>
            </div>
        )
    },
    {
        icon: DatabaseFill,
        iconClass: 'll-ic-amber',
        tag: 'Validate',
        title: 'Data Validation',
        description: 'Compare databases and files across Postgres, MySQL, Oracle, Snowflake, and CSV, then export the evidence for release review.',
        highlights: ['Cross-database compare', 'Mismatch triage', 'Exportable evidence'],
        demo: (
            <div className="ll-data-demo">
                {[
                    ['customer_id', '100%'],
                    ['invoice_total', '97%'],
                    ['status_code', '91%']
                ].map(([field, match]) => (
                    <div key={field}>
                        <span>{field}</span>
                        <em>{match}</em>
                    </div>
                ))}
            </div>
        )
    },
    {
        icon: LayersFill,
        iconClass: 'll-ic-green',
        tag: 'Validate',
        title: 'Synthetic Datasets',
        description: 'Edge-case data variants generated to match your schemas, covering paths your production data never will.',
        highlights: ['Schema-matched variants', 'Edge case coverage', 'Ready for execution'],
        demo: (
            <div className="ll-synth-demo">
                {['Boundary values', 'Invalid formats', 'Localized names', 'Rare combinations'].map((item) => (
                    <span key={item}>{item}</span>
                ))}
            </div>
        )
    },
    {
        icon: BarChartFill,
        iconClass: 'll-ic-violet',
        tag: 'Prove',
        title: 'Release-Ready Analytics',
        description: 'Coverage trends, job history, and readiness signals rolled into dashboards your leadership will actually read.',
        highlights: ['Coverage trends', 'Job and usage history', 'Readiness signals'],
        demo: (
            <div className="ll-chart ll-chart-live">
                {[42, 58, 50, 72, 66, 88, 96].map((height, index) => (
                    <span key={index} style={{ '--ll-bar': `${height}%`, animationDelay: `${index * 90}ms` } as React.CSSProperties} />
                ))}
            </div>
        )
    }
];

const WORKFLOW_STEPS = [
    {
        step: '1',
        title: 'Upload requirements',
        description: 'Documents, specs, or Azure DevOps work items. Agents map your domain on ingest.'
    },
    {
        step: '2',
        title: 'Agents draft coverage',
        description: 'Structured test cases and Gherkin scenarios appear in minutes, fully traceable.'
    },
    {
        step: '3',
        title: 'Sentinels review',
        description: 'Independent reviewer agents reject weak drafts and force retries automatically.'
    },
    {
        step: '4',
        title: 'Runs heal themselves',
        description: 'Playwright agents execute in the browser and repair broken steps mid-run.'
    },
    {
        step: '5',
        title: 'You ship with evidence',
        description: 'Screenshots, logs, data validation, and analytics roll into a readiness story.'
    }
];

function LandingDaylight(): React.ReactElement
{
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [activeFeature, setActiveFeature] = useState(0);

    useEffect(() =>
    {
        const observer = new IntersectionObserver((entries) =>
        {
            entries.forEach((entry) =>
            {
                if (entry.isIntersecting)
                {
                    entry.target.classList.add('ll-visible');
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.15 });
        document.querySelectorAll('.ll-reveal').forEach((element) => observer.observe(element));
        return () => observer.disconnect();
    }, []);

    useEffect(() =>
    {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches)
        {
            return undefined;
        }
        const timer = window.setInterval(() =>
        {
            setActiveFeature((current) => (current + 1) % PLATFORM_FEATURES.length);
        }, 5000);
        return () => window.clearInterval(timer);
    }, [activeFeature]);

    function handleGetStarted(): void
    {
        navigate(isAuthenticated ? '/dashboard' : '/early-access');
    }

    function scrollToSection(id: string): void
    {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }

    return (
        <div className="ll-root">
            <LandingHeader
                onNavigateHome={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                onNavigateSection={scrollToSection}
            />

            <header className="ll-hero">
                <div className="ll-hero-glow" aria-hidden="true" />
                <div className="ll-hero-grid" aria-hidden="true" />
                <div className="ll-hero-inner">
                    <div className="ll-badge">
                        <StarFill />
                        <span>AI agents for quality engineering</span>
                    </div>
                    <h1>
                        Quality engineering,
                        <br />
                        <span className="ll-highlight">on autopilot</span>
                    </h1>
                    <p>
                        AcceliQA reads your requirements, drafts your test suite, runs it in the
                        browser, and hands you the evidence. Your team reviews instead of typing.
                    </p>
                    <div className="ll-hero-actions">
                        <button className="ll-btn ll-btn-primary ll-btn-lg" type="button" onClick={handleGetStarted}>
                            Get early access
                            <ArrowRight />
                        </button>
                        <button className="ll-btn ll-btn-glass ll-btn-lg" type="button" onClick={() => scrollToSection('ll-features')}>
                            Explore the platform
                        </button>
                    </div>
                    <div className="ll-hero-chips">
                        {['Test cases in minutes', 'Sentinel-reviewed output', 'Self-healing runs'].map((item) => (
                            <span key={item}>
                                <CheckCircleFill />
                                {item}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="ll-window-wrap">
                    <MascotImage
                        className="ll-mascot-peek"
                        sources={['/landing/mascot-peek.png']}
                        alt="AcceliQA mascot peeking over the app window"
                    />
                    <div className="ll-window" role="img" aria-label="Preview of the AcceliQA workspace">
                        <div className="ll-window-bar">
                            <span />
                            <span />
                            <span />
                            <em>acceliqa.accelirate.com</em>
                        </div>
                        <div className="ll-window-body">
                            <aside className="ll-window-side" aria-hidden="true">
                                <FileEarmarkTextFill />
                                <Clipboard2CheckFill />
                                <PlayCircleFill />
                                <DatabaseFill />
                                <BarChartFill />
                            </aside>
                            <div className="ll-window-main">
                                <div className="ll-window-head">
                                    <div>
                                        <span>Checkout Flow</span>
                                        <strong>Release readiness</strong>
                                    </div>
                                    <div className="ll-progress" aria-hidden="true">
                                        <span>Generating</span>
                                        <div className="ll-progress-track">
                                            <div className="ll-progress-fill" />
                                        </div>
                                        <em>87%</em>
                                    </div>
                                </div>
                                <div className="ll-window-kpis">
                                    <article>
                                        <strong>112</strong>
                                        <span>test cases</span>
                                    </article>
                                    <article>
                                        <strong>94%</strong>
                                        <span>coverage mapped</span>
                                    </article>
                                    <article>
                                        <strong>18</strong>
                                        <span>runs with evidence</span>
                                    </article>
                                </div>
                                <div className="ll-window-table">
                                    {WINDOW_ROWS.map((row) => (
                                        <div key={row.name} className="ll-window-row">
                                            <span>{row.name}</span>
                                            <em className={`ll-pill ll-pill-${row.status}`}>
                                                {row.status === 'passed' ? 'Passed' : row.status === 'healed' ? 'Self-healed' : 'In review'}
                                            </em>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <section className="ll-band" aria-label="Key metrics">
                <div className="ll-container ll-band-panel ll-reveal">
                    <div className="ll-band-live">
                        <span className="ll-radar" aria-hidden="true">
                            <span />
                            <span />
                            <span />
                        </span>
                        <strong>Agents at work</strong>
                        <em>live across every project</em>
                    </div>
                    <div className="ll-band-stats">
                        {[
                            { end: 10, suffix: 'x', decimals: 0, fill: 0.86, label: 'faster test authoring' },
                            { end: 99.2, suffix: '%', decimals: 1, fill: 0.99, label: 'data match precision' },
                            { end: 5, suffix: '', decimals: 0, fill: 0.72, label: 'agent stages per pipeline' },
                            { end: 24, suffix: '/7', decimals: 0, fill: 0.93, label: 'agents on duty' }
                        ].map((stat, index) => (
                            <div
                                key={stat.label}
                                className="ll-band-stat"
                                style={{ '--ll-off': String(213.6 * (1 - stat.fill)), transitionDelay: `${index * 150}ms` } as React.CSSProperties}
                            >
                                <div className="ll-gauge" aria-hidden="true">
                                    <svg viewBox="0 0 80 80">
                                        <defs>
                                            <linearGradient id={`ll-gauge-grad-${index}`} x1="0" y1="0" x2="1" y2="1">
                                                <stop offset="0%" stopColor="#57c4ff" />
                                                <stop offset="100%" stopColor="#a58cff" />
                                            </linearGradient>
                                        </defs>
                                        <circle className="ll-gauge-track" cx="40" cy="40" r="34" />
                                        <circle className="ll-gauge-fill" cx="40" cy="40" r="34" stroke={`url(#ll-gauge-grad-${index})`} />
                                    </svg>
                                    <div className="ll-gauge-value">
                                        <strong><CountUp end={stat.end} decimals={stat.decimals} suffix={stat.suffix} /></strong>
                                    </div>
                                </div>
                                <span className="ll-gauge-label">{stat.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="ll-section ll-platform-section" id="ll-features">
                <div className="ll-spiral" aria-hidden="true" />
                <div className="ll-container">
                    <div className="ll-heading ll-reveal">
                        <span className="ll-eyebrow">The platform</span>
                        <h2>One workspace, the whole QA motion</h2>
                        <p>Nine capabilities, one product, one shared project context. Pick any capability to see it in action.</p>
                    </div>

                    <div className="ll-explorer ll-reveal">
                        <div className="ll-explorer-rail" role="tablist" aria-label="Platform features">
                            {PLATFORM_FEATURES.map((feature, index) => {
                                const Icon = feature.icon;
                                return (
                                    <button
                                        key={feature.title}
                                        type="button"
                                        role="tab"
                                        aria-selected={index === activeFeature}
                                        className={`ll-rail-item${index === activeFeature ? ' ll-rail-active' : ''}`}
                                        onClick={() => setActiveFeature(index)}
                                    >
                                        <span className={`ll-tile-icon ${feature.iconClass}`}><Icon /></span>
                                        <span className="ll-rail-text">
                                            <strong>{feature.title}</strong>
                                            <em>{feature.tag}</em>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="ll-feature-stage" key={PLATFORM_FEATURES[activeFeature].title}>
                            <div className="ll-stage-head">
                                <span className={`ll-tile-icon ${PLATFORM_FEATURES[activeFeature].iconClass}`}>
                                    {React.createElement(PLATFORM_FEATURES[activeFeature].icon)}
                                </span>
                                <div>
                                    <em>{PLATFORM_FEATURES[activeFeature].tag}</em>
                                    <h3>{PLATFORM_FEATURES[activeFeature].title}</h3>
                                </div>
                            </div>
                            <p>{PLATFORM_FEATURES[activeFeature].description}</p>
                            <div className="ll-stage-body">
                                <div className="ll-stage-demo" aria-hidden="true">
                                    {PLATFORM_FEATURES[activeFeature].demo}
                                </div>
                            </div>
                            <div className="ll-stage-points">
                                {PLATFORM_FEATURES[activeFeature].highlights.map((item) => (
                                    <div key={item}>
                                        <CheckCircleFill />
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="ll-section ll-workflow-section" id="ll-workflow">
                <div className="ll-container">
                    <div className="ll-heading ll-reveal">
                        <span className="ll-eyebrow">How it works</span>
                        <h2>Five steps from document to done</h2>
                    </div>
                    <div className="ll-timeline">
                        <div className="ll-timeline-line" aria-hidden="true" />
                        {WORKFLOW_STEPS.map((item, index) => (
                            <article
                                key={item.step}
                                className={`ll-tl-item ${index % 2 === 0 ? 'll-tl-left' : 'll-tl-right'} ll-reveal`}
                                style={{ transitionDelay: `${index * 90}ms` }}
                            >
                                <span className="ll-tl-node">{item.step}</span>
                                <div className="ll-tl-card">
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="ll-section ll-trust-section" id="ll-trust">
                <div className="ll-container">
                    <div className="ll-heading ll-reveal">
                        <span className="ll-eyebrow">Trust, engineered in</span>
                        <h2>Nothing reaches you unreviewed</h2>
                        <p>Every generation agent is paired with an independent Sentinel reviewer. Weak drafts get rejected and retried before you ever see them.</p>
                    </div>
                    <div className="ll-trust-flow ll-reveal">
                        <div className="ll-trust-card">
                            <span className="ll-tile-icon ll-ic-blue"><LightningChargeFill /></span>
                            <h3>Agent drafts</h3>
                            <p>Requirements, test cases, Gherkin, data, and runs are produced by specialist agents.</p>
                        </div>
                        <div className="ll-trust-arrow" aria-hidden="true">
                            <ArrowRight />
                            <span>reviewed by</span>
                        </div>
                        <div className="ll-trust-card ll-trust-card-sentinel">
                            <span className="ll-tile-icon ll-ic-amber"><ShieldCheck /></span>
                            <h3>Sentinel approves</h3>
                            <p>An independent reviewer checks every artifact and forces retries until it passes.</p>
                        </div>
                        <div className="ll-trust-arrow" aria-hidden="true">
                            <ArrowRight />
                            <span>then</span>
                        </div>
                        <div className="ll-trust-card">
                            <span className="ll-tile-icon ll-ic-green"><CheckCircleFill /></span>
                            <h3>You decide</h3>
                            <p>You stay the final reviewer with versioned edits and full traceability.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="ll-section ll-cta-section">
                <div className="ll-container">
                    <div className="ll-cta ll-reveal">
                        <div className="ll-cta-copy">
                            <h2>Put your QA on autopilot</h2>
                            <p>Bring a requirements document to your first session. Leave with a reviewed test suite.</p>
                            <div className="ll-hero-actions">
                                <button className="ll-btn ll-btn-light ll-btn-lg" type="button" onClick={handleGetStarted}>
                                    Get early access
                                    <ArrowRight />
                                </button>
                                <button className="ll-btn ll-btn-glass ll-btn-lg" type="button" onClick={() => navigate('/contact')}>
                                    Talk to us
                                </button>
                            </div>
                        </div>
                        <div className="ll-cta-visual" aria-hidden="true">
                            <MascotImage
                                className="ll-mascot-cta"
                                sources={['/landing/mascot.png']}
                                alt="AcceliQA mascot"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
}

export default LandingDaylight;
